/* eslint-disable no-unused-expressions */
/* eslint-disable no-param-reassign */
import React, { useState, useEffect } from 'react'
import {
  Col,
  Row,
  Card,
  CardBody,
  Container,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
} from 'reactstrap'
import classnames from 'classnames'
import { useTranslation } from 'react-i18next'

import './components/style.scss'
import { useSelector } from 'react-redux'
import ChangePassword from './components/ChangePassword'
import Payout from './components/Payout'
import ChangeEmail from './components/ChangeEmail'
import Modal from './components/ConfirmationModal'
import { SettingsProps } from './SettingsProps'
import { getUserSelector } from '../../Redux/selectors/auth'
import {
  locationEvent,
  pageInfo,
  pageViewEvent,
} from '../../Helpers/tagManager'
import { Roles } from '../../Constants/enums'
import { TabsWrapper } from './components/styles'

const Settings: React.FunctionComponent<SettingsProps> = ({
  formStates,
  currencyForm,
  emailForm,
  isArtist,
  changeCuratorPassword,
  changeArtistPassword,
  changeEmail,
  match,
  getTransferwiseAccountRequirements,
  getCurrencies,
  currencies,
  requirements,
  specifyTransferwiseAccountRequirements,
  bankDetailsForm,
  setRequirementsToInitialState,
  addTransferwiseRecipient,
  deleteArtistAccount,
  deleteCuratorAccount,
  confirmForm,
  countries,
  getCountries,
  recipient,
}) => {
  const { t } = useTranslation()

  const user = useSelector(getUserSelector)

  const [activeTab, setActiveTab] = useState('1')
  const [passwordChanged, setPasswordChanged] = useState(false)
  const [emailChanged, setEmailChanged] = useState(false)
  const [emailUsed, setEmailUsed] = useState(false)
  const [modal, setModal] = useState(false)
  const [isDeletionError, setIsDeletionError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    user && pageViewEvent(user, pageInfo.settings)
    user && locationEvent()

    const param = match.params.page
    if (param === 'email') {
      setActiveTab('2')
    } else if (param === 'payout') {
      setActiveTab('3')
    } else if (!isArtist) {
      setActiveTab('3')
    }

    if (user?.role === Roles.curator || !isArtist) {
      getCountries()
      getCurrencies()
    }

    return () => {
      setRequirementsToInitialState()
    }
  }, [user])

  const toggle = (tab: string) => {
    activeTab !== tab ? setActiveTab(tab) : null
  }
  const modalToggle = () => setModal(!modal)

  const handleSubmit = () => {
    if (isArtist) {
      changeArtistPassword(formStates.password)
    } else {
      changeCuratorPassword(formStates.password)
    }
    formStates.password = ''
    formStates.confirm = ''
    setPasswordChanged(true)
  }

  const handleChangeEmail = () => {
    changeEmail(emailForm.email, error => {
      if (error) {
        setEmailUsed(true)
        emailForm.email = ''
      } else {
        setEmailUsed(false)
        setEmailChanged(true)
        emailForm.email = ''
      }
    })
  }

  const handleDeleteAccount = () => {
    if (confirmForm?.deletion === 'DELETE') {
      setIsLoading(true)
      if (isArtist) {
        deleteArtistAccount(error => {
          if (error) {
            setIsLoading(false)
            setIsDeletionError(true)
          } else {
            localStorage.clear()
            window.location.replace('/')
          }
        })
      } else {
        deleteCuratorAccount(error => {
          if (error) {
            setIsLoading(false)
            setIsDeletionError(true)
          } else {
            localStorage.clear()
            window.location.replace('/')
          }
        })
      }
    }
  }

  return (
    <Container>
      <Modal
        fade={false}
        modal={modal}
        toggle={modalToggle}
        isError={isDeletionError}
        isLoading={isLoading}
        onSubmit={handleDeleteAccount}
      />
      <Col className="artist_settings" lg={12} md={12} xl={12}>
        <Row>
          <Col md={12}>
            <h3 className="page-title">{t('settingsPage.pageTitle')}</h3>
          </Col>
        </Row>
        <Card className="tabs-wrapper">
          <CardBody>
            <div className="tabs tabs--bordered-bottom tabs-reviews">
              <TabsWrapper>
                <div className="tabs__wrap">
                  <Nav tabs>
                    {isArtist && (
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === '1' })}
                          onClick={() => {
                            toggle('1')
                          }}
                        >
                          {t('settingsPage.changePassword')} &ensp;
                        </NavLink>
                      </NavItem>
                    )}
                    {isArtist && (
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === '2' })}
                          onClick={() => {
                            toggle('2')
                          }}
                        >
                          {t('settingsPage.changeEmail')} &ensp;
                        </NavLink>
                      </NavItem>
                    )}
                    {!isArtist && (
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === '3' })}
                          onClick={() => {
                            toggle('3')
                          }}
                        >
                          {t('settingsPage.payout')} &ensp;
                        </NavLink>
                      </NavItem>
                    )}
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '4' })}
                        onClick={() => {
                          toggle('4')
                        }}
                      >
                        {t('settingsPage.delete')} &ensp;
                      </NavLink>
                    </NavItem>
                  </Nav>
                </div>
              </TabsWrapper>
            </div>
            <TabContent activeTab={activeTab}>
              {isArtist && (
                <TabPane tabId="1">
                  <ChangePassword
                    passwordChanged={passwordChanged}
                    onSubmit={handleSubmit}
                  />
                </TabPane>
              )}
              {isArtist && (
                <TabPane tabId="2">
                  <ChangeEmail
                    emailChanged={emailChanged}
                    emailUsed={emailUsed}
                    onSubmit={handleChangeEmail}
                  />
                </TabPane>
              )}
              {!isArtist && (
                <TabPane tabId="3">
                  <Payout
                    addTransferwiseRecipient={addTransferwiseRecipient}
                    bankDetailsForm={bankDetailsForm}
                    countries={countries}
                    currencies={currencies}
                    currencyForm={currencyForm}
                    getTransferwiseAccountRequirements={
                      getTransferwiseAccountRequirements
                    }
                    recipient={recipient}
                    requirements={requirements}
                    specifyTransferwiseAccountRequirements={
                      specifyTransferwiseAccountRequirements
                    }
                  />
                </TabPane>
              )}
              <TabPane tabId="4">
                <div style={{ marginTop: '10px', marginLeft: '10px' }}>
                  <p>{t('settingsPage.deleteInfo')}</p>
                  <p style={{ margin: '0px' }}>
                    {t('settingsPage.deleteInfo1')}
                  </p>
                </div>
                <Button
                  className="btn-delete"
                  color="danger"
                  onClick={modalToggle}
                >
                  {t('settingsPage.delete')}
                </Button>
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </Col>
    </Container>
  )
}

export default Settings
